/**
 * Synthetic tenant + edge generator for the AIM pivot prototype.
 *
 * PROTOTYPE ONLY — delete with the mock interceptor when the real edge table
 * lands.
 *
 * Two properties matter here, and both are deliberate:
 *
 * 1. DETERMINISTIC. Every edge is a pure function of the node ids and the scale
 *    key, via a seeded hash. Nothing uses `Math.random()`. Re-rendering, or
 *    pivoting away and back, must not reshuffle the graph — a layout that moves
 *    under the cursor is unevaluable, and a demo that looks different on every
 *    reload tells you nothing.
 *
 * 2. LAZY. There is no materialised edge list. Neighbours are computed on demand
 *    from the focus id, so generation costs O(degree) rather than O(edges).
 *    That is not just an optimisation — at the `large` profile the endpoint→app
 *    relation alone is ~600k edges, which is exactly the number that must never
 *    reach a browser. The generator is built the way the real backend has to be
 *    built: answer per node, never ship the graph.
 *
 * Relations are also SYMMETRIC by construction. `hasApp(e, a)` is one predicate
 * consulted from both directions, so pivoting endpoint → app → endpoint lands
 * you back where you started. Faking the two directions independently produces a
 * graph that contradicts itself the moment anyone explores it.
 */

import {
  collapseThreshold,
  EMPTY_FILTERS,
  groupBudget,
  hasActiveFilters,
  isFilterableKind,
  MAX_RING_MEMBERS,
  SEVERITY_CVSS_RANGE,
  SEVERITY_LABELS,
  SEVERITY_ORDER,
  VulnSeverity,
  PivotAggregate,
  PivotAxis,
  PivotCell,
  PivotCollapseNotice,
  PivotCube,
  CubeMeasure,
  PivotDegree,
  PivotEdge,
  PivotLens,
  PivotMemberRow,
  classificationLabel,
  PivotFacets,
  PivotFacetValue,
  PivotFilters,
  PivotNeighborhood,
  PivotRelationSummary,
  PivotNode,
  PivotNodeKind,
  PivotRing,
  PivotSlice,
  SCALE_PROFILES,
  ScaleProfileKey,
  TenantGap,
  ThreatAssessment,
  ThreatFactor,
  ThreatReason,
  ThreatTier,
} from '../models/pivot.models';

// ────────────────────────────────────────────────────────────────────────────
// Seeded pseudo-randomness
// ────────────────────────────────────────────────────────────────────────────

/** FNV-1a. Cheap, stable, good enough spread for layout purposes. */
function hashString(input: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < input.length; i++) {
    h ^= input.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Deterministic float in [0, 1) from any set of key parts. */
function rand(...parts: (string | number)[]): number {
  const h = hashString(parts.join(':'));
  // mulberry32 single step
  let t = (h + 0x6d2b79f5) >>> 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}

/** Deterministic integer in [min, max]. */
function randInt(min: number, max: number, ...parts: (string | number)[]): number {
  return min + Math.floor(rand(...parts) * (max - min + 1));
}

/** Deterministic pick from a weighted distribution. */
function weightedPick(weights: number[], ...parts: (string | number)[]): number {
  const total = weights.reduce((a, b) => a + b, 0);
  let r = rand(...parts) * total;
  for (let i = 0; i < weights.length; i++) {
    r -= weights[i];
    if (r <= 0) return i;
  }
  return weights.length - 1;
}

// ────────────────────────────────────────────────────────────────────────────
// Synthetic tenant
// ────────────────────────────────────────────────────────────────────────────

const FIRST_NAMES = ['Jordan', 'Priya', 'Marcus', 'Aisha', 'Tomas', 'Lena', 'Devon', 'Yuki', 'Rafael', 'Nadia', 'Callum', 'Imani', 'Soren', 'Elif', 'Mateo', 'Rowan'];
const LAST_NAMES = ['Vance', 'Rao', 'Okafor', 'Bennett', 'Lindqvist', 'Moreau', 'Castellanos', 'Tanaka', 'Whitfield', 'Adeyemi', 'Brennan', 'Sorensen', 'Ferreira', 'Nakamura', 'Delgado', 'Ashworth'];
const DEPARTMENTS = ['Finance', 'Engineering', 'Sales', 'Legal', 'Operations', 'HR', 'Marketing', 'Support'];
const OS_NAMES = ['Windows 11 Pro', 'Windows 10 Pro', 'macOS Sonoma', 'macOS Ventura', 'Ubuntu Server 22.04', 'Red Hat Enterprise Linux 9', 'Windows Server 2022', 'Debian 12', 'Windows Server 2019', 'Amazon Linux 2023', 'macOS Sequoia', 'Ubuntu Desktop 24.04', 'CentOS Stream 9', 'SUSE Linux 15', 'Windows 11 Enterprise', 'Rocky Linux 9', 'Fedora 40', 'Oracle Linux 9', 'macOS Monterey', 'Windows Server 2016', 'AlmaLinux 9', 'FreeBSD 14'];
const APP_NAMES = ['Google Chrome', 'Microsoft Edge', 'Microsoft Teams', 'Zoom', 'Slack', 'Adobe Acrobat Reader', 'Mozilla Firefox', '7-Zip', 'Notepad++', 'Visual Studio Code', 'Python', 'Node.js', 'Git', 'Docker Desktop', 'PuTTY', 'WinSCP', 'VLC Media Player', 'Java Runtime', 'Microsoft Office', 'OneDrive', 'Dropbox', 'Postman', 'Wireshark', 'FileZilla', 'TeamViewer', 'AnyDesk', 'Citrix Workspace', 'VMware Tools', 'Salesforce Desktop', 'Tableau Desktop'];
const TAGS = ['VIP', 'Production', 'Critical', 'Spare', 'Contractor', 'Kiosk', 'Executive', 'Lab'];
const CLASSIFICATIONS = ['privileged_access', 'service_account', 'shared_device', 'byod', 'pci_scope'];
const VULN_TITLES = ['Remote code execution in OpenSSL', 'Privilege escalation in Print Spooler', 'Outdated TLS configuration', 'Unpatched Chrome sandbox escape', 'SMB signing not required', 'Kerberos delegation misconfiguration', 'Log4j deserialisation', 'Local admin rights not restricted', 'Stale service account credential', 'Unencrypted disk volume'];
const CASE_TITLES = ['Suspicious sign-in from new geography', 'Phishing email reported', 'Malware quarantined on endpoint', 'Impossible travel detected', 'MFA fatigue attempt', 'Unusual data egress volume'];
const ADVISORY_CITIES = ['Lagos', 'Bogotá', 'Manila', 'Istanbul', 'Cairo', 'Karachi', 'Nairobi', 'Caracas'];

/** Sources per entity type, mirroring SOURCES_BY_ENTITY_TYPE in inventory.models.ts. */
const SOURCES: Record<string, string[]> = {
  endpoint: ['EDR (SentinelOne)', 'XDR (Stellar Cyber)', 'Vicarius'],
  user: ['XDR (Stellar Cyber)', 'Entra ID', 'Tickets', 'Cyflare XRAI'],
  application: ['EDR (SentinelOne)'],
  operating_system: ['EDR (SentinelOne)', 'Vicarius'],
};

export interface SyntheticTenant {
  key: ScaleProfileKey;
  userCount: number;
  endpointCount: number;
  appCount: number;
  osCount: number;
  /** Relations with no source feeding them, and the integration that fixes each. */
  gaps: TenantGap[];
}

const tenantCache = new Map<ScaleProfileKey, SyntheticTenant>();

export function getTenant(key: ScaleProfileKey): SyntheticTenant {
  const cached = tenantCache.get(key);
  if (cached) return cached;
  const p = SCALE_PROFILES[key];
  const tenant: SyntheticTenant = {
    key,
    userCount: p.users,
    endpointCount: p.endpoints,
    appCount: p.applications,
    osCount: Math.min(p.operatingSystems, OS_NAMES.length),
    gaps: p.gaps,
  };
  tenantCache.set(key, tenant);
  return tenant;
}

/**
 * Gaps that apply to a given focus kind — the chips that render as blind rather
 * than absent.
 *
 * Only an endpoint fans out to software, so only an endpoint can show the
 * software blind spot; a user's neighbourhood is unaffected by the EDR gap.
 */
export function gapsForKind(t: SyntheticTenant, kind: PivotNodeKind): TenantGap[] {
  if (kind !== 'endpoint') return [];
  return t.gaps;
}

// ────────────────────────────────────────────────────────────────────────────
// Entity attribute derivation (all pure functions of id)
// ────────────────────────────────────────────────────────────────────────────

export function userName(t: SyntheticTenant, id: number): string {
  const f = FIRST_NAMES[randInt(0, FIRST_NAMES.length - 1, t.key, 'uf', id)];
  const l = LAST_NAMES[randInt(0, LAST_NAMES.length - 1, t.key, 'ul', id)];
  return `${f} ${l}`;
}

export function userEmail(t: SyntheticTenant, id: number): string {
  return `${userName(t, id).toLowerCase().replace(/\s+/g, '.')}${id}@contoso.com`;
}

export function userDepartment(t: SyntheticTenant, id: number): string {
  return DEPARTMENTS[randInt(0, DEPARTMENTS.length - 1, t.key, 'dept', id)];
}

/** ~19% Guest accounts, matching the recon figure in inventory.models.ts:153. */
export function isExternalUser(t: SyntheticTenant, id: number): boolean {
  return rand(t.key, 'ext', id) < 0.19;
}

/** Servers and shared machines are the multi-user cases; workstations are not. */
export function isServerEndpoint(t: SyntheticTenant, id: number): boolean {
  return rand(t.key, 'srv', id) < 0.18;
}

/** Kiosks, lab machines, hot desks — shared, but not servers. */
export function isSharedEndpoint(t: SyntheticTenant, id: number): boolean {
  return !isServerEndpoint(t, id) && rand(t.key, 'shared', id) < 0.09;
}

export function endpointName(t: SyntheticTenant, id: number): string {
  if (isServerEndpoint(t, id)) {
    return `SRV-${['DB', 'WEB', 'APP', 'FILE', 'DC'][randInt(0, 4, t.key, 'srvk', id)]}-${String(id).padStart(3, '0')}`;
  }
  const owner = endpointOwner(t, id);
  const initials = owner === null
    ? 'SHARED'
    : userName(t, owner).split(' ').map((s) => s[0]).join('') + String(owner).slice(-2);
  const prefix = rand(t.key, 'plat', id) < 0.7 ? 'DESKTOP' : 'MBP';
  return `${prefix}-${initials}${String(id).padStart(3, '0')}`;
}

/**
 * Indices into OS_NAMES that are server operating systems.
 *
 * Without this the OS was drawn independently of machine class, which produced
 * `SRV-WEB-815 · macOS Sonoma` — a web server running a desktop OS. Nobody
 * reads past a detail like that; it makes every other number on the screen look
 * invented too.
 */
const SERVER_OS_RANKS = new Set([4, 5, 6, 7, 8, 9, 12, 13, 15, 17, 19, 20, 21]);

const osPoolCache = new Map<string, number[]>();

/**
 * The OS ranks available to a machine class within this tenant.
 *
 * Constrained to `osCount`, since a small tenant only has the first N entries.
 * Falls back to the full range if a class has no eligible OS at that size, so a
 * tiny tenant can never end up with serverless servers.
 */
function osPool(t: SyntheticTenant, server: boolean): number[] {
  const key = `${t.key}:${server}`;
  const cached = osPoolCache.get(key);
  if (cached) return cached;
  const pool: number[] = [];
  for (let i = 0; i < t.osCount; i++) {
    if (SERVER_OS_RANKS.has(i) === server) pool.push(i);
  }
  const result = pool.length ? pool : Array.from({ length: t.osCount }, (_, i) => i);
  osPoolCache.set(key, result);
  return result;
}

/**
 * OS rank for an endpoint, or -1 when the tenant has no OS data at all.
 *
 * -1 is a real case, not a defensive branch: a Defender-only tenant produces
 * zero `operating_system` entities however many endpoints it has, because the
 * vertical is fed by SentinelOne and Vicarius only. Callers must skip the
 * relation rather than render an "Unknown OS" node, which would claim we looked
 * and found nothing.
 */
export function endpointOs(t: SyntheticTenant, id: number): number {
  if (t.osCount === 0) return -1;
  // Rank-biased within the machine's own class, so servers get server OSes and
  // laptops get desktop ones.
  const pool = osPool(t, isServerEndpoint(t, id));
  if (!pool.length) return -1;
  const r = rand(t.key, 'os', id);
  return pool[Math.min(pool.length - 1, Math.floor(Math.pow(r, 2.2) * pool.length))];
}

export function osName(t: SyntheticTenant, rank: number): string {
  return OS_NAMES[rank % OS_NAMES.length];
}

export function appName(t: SyntheticTenant, rank: number): string {
  if (rank < APP_NAMES.length) return APP_NAMES[rank];
  const vendor = LAST_NAMES[rank % LAST_NAMES.length];
  const product = ['Suite', 'Agent', 'Client', 'Manager', 'Toolkit', 'Console'][rank % 6];
  return `${vendor} ${product} ${(rank % 9) + 1}.0`;
}

/**
 * Zipf-ish install share for an application by popularity rank. Rank 0 (Chrome)
 * sits on ~95% of endpoints — that is the hub node the orbit's collapse rule has
 * to survive, and it is why the rule exists.
 */
export function appPopularity(rank: number): number {
  // Exponent tuned so the resulting apps-per-endpoint lands in the 40–200 band
  // real EDR inventories show. It was 0.55 first, which produced a median of 16
  // installs per endpoint at small scale — an unrealistically thin ring that
  // made the collapse rule look unnecessary.
  return Math.min(0.95, 0.95 / Math.pow(rank + 1, 0.35));
}

/**
 * The single install predicate, consulted from both directions so
 * endpoint→app and app→endpoint can never disagree.
 */
export function hasApp(t: SyntheticTenant, endpointId: number, appRank: number): boolean {
  return rand(t.key, 'app', endpointId, appRank) < appPopularity(appRank);
}

/**
 * Owning user of an endpoint, or null for servers / shared / unattributable
 * devices.
 *
 * This is the KEYSTONE EDGE and the one that does not exist in production —
 * SentinelOne `EdrEndpointData` has no user field (inventory.models.ts:373), so
 * a real implementation has to bridge via XDR UEBA `principle_name` at ~57–87%
 * coverage.
 *
 * The null rate here lands around 40% once servers and Guest-owned candidates
 * are excluded — squarely inside the 13–43% unattributable band that XDR
 * coverage implies. Modelling that gap is the point: if the pivot only looks
 * good when every endpoint has an owner, it will not survive the real resolver.
 */
export function endpointOwner(t: SyntheticTenant, endpointId: number): number | null {
  return usersOfEndpoint(t, endpointId).find((l) => l.role === 'primary')?.userId ?? null;
}

/** Confidence of the primary user↔endpoint attribution. Never 1.0 — see above. */
export function ownershipConfidence(t: SyntheticTenant, endpointId: number): number {
  return 0.55 + rand(t.key, 'ownconf', endpointId) * 0.40;
}

/** One user's association with one machine. */
export interface EndpointUserLink {
  userId: number;
  /** `primary` = the habitual interactive user; `observed` = also seen logging on. */
  role: 'primary' | 'observed';
  confidence: number;
}

/**
 * Every user associated with a machine — NOT just one.
 *
 * The first cut modelled ownership as a single nullable owner, which quietly
 * asserted that a machine has at most one user. That is wrong for exactly the
 * machines that matter most in a pivot: a domain controller or file server has
 * a whole admin population, and kiosks and lab machines are shared by design.
 * It also broke the most interesting navigation in the product — user → their
 * server → *the other people on that server* → one of them — because step two
 * had nothing to show.
 *
 * Cardinality by machine class:
 *   server      3–12 users (admins + service accounts)
 *   shared      2–5 users
 *   workstation 1 primary, occasionally a second (hot desk)
 *   ~30%        no resolvable user at all — the XDR coverage gap
 *
 * Role matters as much as count. A server's "primary" is whoever logs on most,
 * which is a far weaker claim than a laptop's primary user, so observed links
 * carry lower confidence and the UI must not flatten the two together.
 */
export function usersOfEndpoint(t: SyntheticTenant, endpointId: number): EndpointUserLink[] {
  // The XDR coverage gap: no principal-name evidence for this machine at all.
  if (rand(t.key, 'owned', endpointId) < 0.30) return [];

  let count: number;
  if (isServerEndpoint(t, endpointId)) count = randInt(3, 12, t.key, 'srvusers', endpointId);
  else if (isSharedEndpoint(t, endpointId)) count = randInt(2, 5, t.key, 'shusers', endpointId);
  else count = rand(t.key, 'hotdesk', endpointId) < 0.12 ? 2 : 1;

  const links: EndpointUserLink[] = [];
  const seen = new Set<number>();
  for (let i = 0; i < count; i++) {
    const candidate = randInt(1, t.userCount, t.key, 'owner', endpointId, i);
    // Guests hold no internal machine associations (inventory.models.ts:153).
    if (isExternalUser(t, candidate) || seen.has(candidate)) continue;
    seen.add(candidate);
    const role = links.length === 0 ? 'primary' : 'observed';
    links.push({
      userId: candidate,
      role,
      confidence:
        role === 'primary'
          ? ownershipConfidence(t, endpointId)
          : 0.42 + rand(t.key, 'obsconf', endpointId, i) * 0.33,
    });
  }
  return links;
}

// ────────────────────────────────────────────────────────────────────────────
// CVEs as SHARED entities
// ────────────────────────────────────────────────────────────────────────────

/**
 * A CVE in this tenant's catalogue.
 *
 * The important change from the first cut: a CVE is a SHARED entity, not a
 * per-endpoint finding. Previously a vulnerability's id was `endpointId * 100 + i`,
 * which made every one of them a leaf of degree 1 — a dot you could not click
 * through, and a model that could never answer "what else is affected?".
 *
 * A CVE now targets a COMPONENT, and endpoints inherit it by having that
 * component installed. That makes the chain the same one applications already
 * walk, in both directions:
 *
 *     CVE → component → endpoints → users
 *
 * and it makes the relation symmetric by construction, which is this file's
 * standing invariant: `affectsEndpoint` is one predicate consulted from both
 * ends, so CVE → endpoint → CVE lands where it started.
 *
 * `device` targets exist because vulnerabilities are not only a software-inventory
 * story — Vicarius observes endpoints directly, so a tenant with no application
 * inventory at all (Defender-only) still has findings. Tying the catalogue purely
 * to installed apps would have zeroed out vulnerabilities for the two largest
 * real tenants, which is a modelling artefact, not a fact about them.
 */
export interface SyntheticCve {
  /** Catalogue index — the shared entity id, stable across endpoints. */
  id: number;
  cve: string;
  title: string;
  severity: VulnSeverity;
  cvss: number;
  target: { kind: 'application' | 'operating_system' | 'device'; rank: number };
  /** On CISA's Known Exploited Vulnerabilities list. */
  kev: boolean;
}

/**
 * Catalogue size. Has a floor independent of `appCount` so that a tenant with no
 * software inventory still carries findings — see `device` targets above.
 */
function cveCatalogSize(t: SyntheticTenant): number {
  return Math.round(120 + t.appCount * 0.06 + t.osCount * 4);
}

/** Severity mix, roughly matching a real vulnerability backlog. */
const SEVERITY_WEIGHTS: [VulnSeverity, number][] = [
  ['critical', 8],
  ['high', 24],
  ['medium', 45],
  ['low', 23],
];

const cveCatalogCache = new Map<ScaleProfileKey, SyntheticCve[]>();

export function cveCatalog(t: SyntheticTenant): SyntheticCve[] {
  const cached = cveCatalogCache.get(t.key);
  if (cached) return cached;

  const size = cveCatalogSize(t);
  const catalog: SyntheticCve[] = [];
  for (let i = 0; i < size; i++) {
    const sev = SEVERITY_WEIGHTS[weightedPick(SEVERITY_WEIGHTS.map(([, w]) => w), t.key, 'cvesev', i)][0];
    const [lo, hi] = SEVERITY_CVSS_RANGE[sev];
    const cvss = Math.round((lo + rand(t.key, 'cvss', i) * (hi - lo)) * 10) / 10;

    // Target selection. Biased HARD toward popular applications: a Chrome CVE
    // legitimately lands on ~95% of the estate, and that long tail is what
    // produces the realistic "this host has 100+ findings" case the old
    // uniform-random count could never reach.
    let target: SyntheticCve['target'];
    const roll = rand(t.key, 'cvetgt', i);
    if (t.appCount > 0 && roll < 0.72) {
      const biased = Math.pow(rand(t.key, 'cverank', i), 2.6);
      target = { kind: 'application', rank: Math.min(t.appCount - 1, Math.floor(biased * t.appCount)) };
    } else if (t.osCount > 0 && roll < 0.88) {
      target = { kind: 'operating_system', rank: randInt(0, t.osCount - 1, t.key, 'cveos', i) };
    } else {
      target = { kind: 'device', rank: 0 };
    }

    catalog.push({
      id: i,
      // Year/number derived from the index so the identifier is stable and looks
      // like a real one. Not parsed anywhere — it is a display string.
      cve: `CVE-${2021 + (i % 5)}-${String(10000 + ((i * 7919) % 89999)).padStart(5, '0')}`,
      title: VULN_TITLES[i % VULN_TITLES.length],
      severity: sev,
      cvss,
      target,
      // KEV is rare and concentrated in the high bands, which is what makes it
      // worth showing at all.
      kev: (sev === 'critical' || sev === 'high') && rand(t.key, 'kev', i) < 0.18,
    });
  }

  cveCatalogCache.set(t.key, catalog);
  return catalog;
}

/**
 * How well patched an endpoint is, 0 (neglected) → 1 (current).
 *
 * Without this, "has the component" alone decided exposure, and because the
 * catalogue is biased toward popular software that meant every host got nearly
 * every CVE: `smb` averaged 44 findings with a floor of 44 and a ceiling of 62.
 * No well-patched machines and no disasters — the two cases anyone triaging
 * actually cares about.
 *
 * Skewed toward patched, because most managed estates mostly are; the tail is
 * where the interesting hosts live.
 */
export function patchLevel(t: SyntheticTenant, endpointId: number): number {
  return Math.pow(rand(t.key, 'patch', endpointId), 0.55);
}

/**
 * Does this CVE affect this endpoint?
 *
 * ONE predicate, consulted from both directions — `vulnsOfEndpoint` and
 * `endpointsOfCve` both route through it, so the relation cannot disagree with
 * itself. Faking the two directions independently produces a graph that
 * contradicts itself the moment anyone explores it.
 */
export function affectsEndpoint(t: SyntheticTenant, cve: SyntheticCve, endpointId: number): boolean {
  const present =
    cve.target.kind === 'application'
      ? hasApp(t, endpointId, cve.target.rank)
      : cve.target.kind === 'operating_system'
        ? endpointOs(t, endpointId) === cve.target.rank
        : rand(t.key, 'devvuln', cve.id, endpointId) < 0.16;
  if (!present) return false;
  // Having the component is necessary, not sufficient — a patched host has the
  // software and not the finding.
  return rand(t.key, 'unpatched', cve.id, endpointId) > patchLevel(t, endpointId);
}

const endpointVulnCache = new Map<string, SyntheticCve[]>();

/** Every CVE affecting one endpoint, worst first. */
export function vulnsOfEndpoint(t: SyntheticTenant, endpointId: number): SyntheticCve[] {
  const key = `${t.key}:${endpointId}`;
  const cached = endpointVulnCache.get(key);
  if (cached) return cached;
  const out = cveCatalog(t).filter((c) => affectsEndpoint(t, c, endpointId));
  out.sort((a, b) => SEVERITY_ORDER.indexOf(a.severity) - SEVERITY_ORDER.indexOf(b.severity) || b.cvss - a.cvss);
  endpointVulnCache.set(key, out);
  return out;
}

export function vulnCount(t: SyntheticTenant, endpointId: number): number {
  return vulnsOfEndpoint(t, endpointId).length;
}

/** Severity histogram for one endpoint — what the pucks are built from. */
export function vulnsBySeverity(t: SyntheticTenant, endpointId: number): Map<VulnSeverity, SyntheticCve[]> {
  const buckets = new Map<VulnSeverity, SyntheticCve[]>();
  for (const c of vulnsOfEndpoint(t, endpointId)) {
    const list = buckets.get(c.severity);
    if (list) list.push(c);
    else buckets.set(c.severity, [c]);
  }
  return buckets;
}

/**
 * Endpoints affected by one CVE — the blast radius, and the reason a CVE is worth
 * being a node at all.
 *
 * Delegates to the component's own fan-out where it can, so a CVE targeting
 * Chrome costs the same as pivoting onto Chrome rather than a full estate scan.
 */
export function endpointsOfCve(t: SyntheticTenant, cveId: number, limit = Infinity): number[] {
  const cve = cveCatalog(t)[cveId];
  if (!cve) return [];

  /**
   * The component narrows the CANDIDATES; `affectsEndpoint` decides.
   *
   * The first cut returned `endpointsOfApp` directly, which was a real
   * asymmetry: that lists every endpoint carrying the component, while
   * `vulnsOfEndpoint` additionally requires the host to be unpatched for this
   * CVE. Measured at 123 contradictions in 200 — a CVE claimed hosts that, asked
   * directly, did not have it. Both directions must route through the single
   * predicate or the graph disagrees with itself as soon as anyone walks it.
   */
  const candidates =
    cve.target.kind === 'application'
      ? endpointsOfApp(t, cve.target.rank)
      : cve.target.kind === 'operating_system'
        ? endpointsOnOs(t, cve.target.rank)
        : null;

  const out: number[] = [];
  if (candidates) {
    for (const e of candidates) {
      if (affectsEndpoint(t, cve, e)) {
        out.push(e);
        if (out.length >= limit) break;
      }
    }
    return out;
  }
  for (let e = 1; e <= t.endpointCount; e++) {
    if (affectsEndpoint(t, cve, e)) {
      out.push(e);
      if (out.length >= limit) break;
    }
  }
  return out;
}

export function countEndpointsOfCve(t: SyntheticTenant, cveId: number): number {
  return endpointsOfCve(t, cveId).length;
}

export function caseCount(t: SyntheticTenant, userId: number): number {
  const r = rand(t.key, 'casec', userId);
  if (r < 0.78) return 0;
  return randInt(1, 4, t.key, 'casen', userId);
}

export function advisoryCount(t: SyntheticTenant, userId: number): number {
  return rand(t.key, 'advc', userId) < 0.03 ? 1 : 0;
}

/** Which sources have seen this entity. Drives the identity ring. */
export function seenBy(t: SyntheticTenant, kind: string, id: number): string[] {
  const applicable = SOURCES[kind] ?? [];
  const seen = applicable.filter((_, i) => rand(t.key, 'seen', kind, id, i) < 0.68);
  // Every entity is seen by at least one source, or it would not be in inventory.
  return seen.length ? seen : [applicable[0]];
}

/**
 * Tags and classifications are drawn from KIND-SPECIFIC pools.
 *
 * A single shared pool put "Kiosk" and "Lab" on people and "Executive" on
 * servers. Nobody reads past a person tagged Kiosk — it makes every other value
 * on the screen look invented, which is exactly the failure the server/OS
 * mismatch caused. Anything genuinely applicable to both stays in both lists.
 */
/**
 * Tag pools, sized to what a real org's tag corpus looks like rather than to
 * what fits neatly in a demo.
 *
 * The first cut had three user tags and five endpoint tags. Eight values total
 * makes a flat checkbox list look like a perfectly good filter UI, and it is
 * not: `EntityTag.tag` is free text with org-wide autocomplete that ACCEPTS NEW
 * VALUES, so a real corpus runs to dozens or hundreds and grows every time an
 * analyst types. A prototype that cannot reproduce that cannot be used to judge
 * the control that has to survive it.
 *
 * These are the categories tags actually get used for — ownership, environment,
 * lifecycle, compliance, location, business unit — which is also why the corpus
 * grows: each is a separate axis someone reached for a tag to express.
 */
const TAGS_BY_KIND: Record<string, string[]> = {
  user: [
    'VIP', 'Executive', 'Contractor', 'Intern', 'Offboarding', 'New hire',
    'Remote', 'On-site', 'Travels frequently', 'Board member', 'Finance',
    'Legal', 'HR', 'Engineering', 'Sales', 'Support', 'Marketing',
    'Privileged', 'Break-glass', 'Shared mailbox', 'Vendor', 'Auditor',
    'MFA exempt', 'Phishing repeat', 'Training overdue',
  ],
  endpoint: [
    'Production', 'Staging', 'Development', 'Critical', 'Spare', 'Kiosk', 'Lab',
    'Domain controller', 'File server', 'Web server', 'Database', 'Jump host',
    'Build agent', 'Backup target', 'Loaner', 'Executive laptop', 'Warehouse',
    'Point of sale', 'Manufacturing', 'HVAC', 'Camera', 'Conference room',
    'Decommission pending', 'Warranty expired', 'No agent', 'Air-gapped',
    'PCI', 'HIPAA', 'SOX', 'Legal hold', 'London office', 'Austin office',
    'Remote worker', 'BYOD approved', 'Encrypted', 'Unencrypted',
  ],
  application: [
    'Approved', 'Unapproved', 'Business critical', 'End of life', 'Shadow IT',
    'Licensed', 'Freeware', 'Remote access', 'Developer tool',
  ],
  operating_system: ['Supported', 'End of life', 'Extended support', 'Golden image'],
};

/**
 * The classification vocabulary, shaped like the real one.
 *
 * Mirrors `ClassificationDefinition` in `inventory_api_v1/models.py`: a
 * Cyflare-curated flag with a stable `key` and an `entity_types` list declaring
 * which entity types it may be applied to (`applies_to()` enforces it at the API
 * layer on write, and the picker only offers applicable flags).
 *
 * Held as DEFINITIONS rather than as a kind → values map because applicability
 * is a property of the definition, and filtering has to be able to ask the
 * question in that direction: "does `pci_scope` apply to users?" — not "what can
 * a user have?".
 *
 * The first cut inverted this, and it produced a real bug. Filtering by
 * `pci_scope` deleted all 24 users from a CVE's neighbourhood, because users
 * could not carry the value so nothing matched. But `pci_scope` does not APPLY
 * to users at all, and "no PCI-scope users" and "users cannot be PCI-scope" are
 * the same two statements this feature already refuses to conflate for CVEs. A
 * filter must leave a kind it does not apply to alone.
 */
export interface ClassificationDef {
  key: string;
  entityTypes: PivotNodeKind[];
}

export const CLASSIFICATION_DEFS: ClassificationDef[] = [
  { key: 'privileged_access', entityTypes: ['user', 'endpoint'] },
  { key: 'service_account', entityTypes: ['user'] },
  { key: 'shared_device', entityTypes: ['endpoint'] },
  { key: 'byod', entityTypes: ['endpoint'] },
  { key: 'pci_scope', entityTypes: ['endpoint'] },
];

/** Whether one classification may be applied to this kind. */
export function classificationApplies(key: string, kind: PivotNodeKind): boolean {
  const def = CLASSIFICATION_DEFS.find((d) => d.key === key);
  // An unknown key applies to nothing — it cannot narrow what it cannot describe.
  return !!def && def.entityTypes.includes(kind);
}

/** Entity types one classification may be applied to, for the facet panel. */
export function classificationEntityTypes(key: string): PivotNodeKind[] {
  return CLASSIFICATION_DEFS.find((d) => d.key === key)?.entityTypes ?? [];
}

const CLASSIFICATIONS_BY_KIND: Record<string, string[]> = Object.fromEntries(
  (['user', 'endpoint', 'application', 'operating_system'] as PivotNodeKind[]).map((k) => [
    k,
    CLASSIFICATION_DEFS.filter((d) => d.entityTypes.includes(k)).map((d) => d.key),
  ]),
);

export function entityTags(t: SyntheticTenant, kind: string, id: number): string[] {
  const pool = TAGS_BY_KIND[kind] ?? TAGS;
  if (!pool.length) return [];
  const n = weightedPick([70, 20, 10], t.key, 'tagn', kind, id);
  const out: string[] = [];
  for (let i = 0; i < n; i++) out.push(pool[randInt(0, pool.length - 1, t.key, 'tag', kind, id, i)]);
  return Array.from(new Set(out));
}

export function entityClassifications(t: SyntheticTenant, kind: string, id: number): string[] {
  const pool = CLASSIFICATIONS_BY_KIND[kind] ?? [];
  // No definition in the vocabulary applies to this kind, so it can hold none.
  // Applications and operating systems are exactly this case today, and the
  // empty pool has to short-circuit: indexing `pool[randInt(0, -1)]` yielded
  // `undefined`, which then travelled all the way into the facet list as a
  // nameless value. The previous `?? CLASSIFICATIONS` fallback hid it by handing
  // out flags from the generic pool that no definition permits on these kinds.
  if (!pool.length) return [];
  if (rand(t.key, 'clsn', kind, id) > 0.35) return [];
  return [pool[randInt(0, pool.length - 1, t.key, 'cls', kind, id)]];
}

function lastSeen(t: SyntheticTenant, kind: string, id: number): string {
  const daysAgo = Math.floor(Math.pow(rand(t.key, 'ls', kind, id), 3) * 45);
  const base = Date.parse('2026-08-10T09:00:00Z');
  return new Date(base - daysAgo * 86_400_000).toISOString();
}

/** Whole days since this entity was last observed. */
function staleDays(t: SyntheticTenant, kind: string, id: number): number {
  return Math.floor(Math.pow(rand(t.key, 'ls', kind, id), 3) * 45);
}

// ────────────────────────────────────────────────────────────────────────────
// Threat tier — the Cyflare-computed severity
// ────────────────────────────────────────────────────────────────────────────

/**
 * Derive an entity's threat tier from context, not from a vendor score.
 *
 * The whole argument for this number is that it is composed from things no
 * single vendor can see:
 *
 *   COVERAGE   — is anything actually watching this? AIM's unique signal.
 *   POSTURE    — managed, current, recently observed, owned by a known person.
 *   EXPOSURE   — how much is wrong with it.
 *   TENDENCY   — has this entity, or the people on it, been in trouble before.
 *   CLASSIFICATION — what the customer told us it is.
 *
 * Every contribution is recorded as a `ThreatReason`, because an unexplained
 * tier is unarguable and therefore untrustworthy. The reasons ARE the feature;
 * the number is just how they sort.
 *
 * Deliberately additive and capped rather than multiplicative: a
 * multiplicative model lets one factor dominate, and the demo-killer is a
 * tier-1 machine whose only sin is being three weeks stale.
 */
export function threatOf(t: SyntheticTenant, kind: PivotNodeKind, id: number): ThreatAssessment {
  const reasons: ThreatReason[] = [];
  const add = (factor: ThreatFactor, label: string, weight: number) => {
    reasons.push({ factor, label, weight });
  };

  const applicable = SOURCES[kind] ?? [];
  const sources = seenBy(t, kind, id);

  // ── Coverage. The blind-spot signal, and the heaviest single factor: an
  // endpoint nothing is watching is worse than one with a known problem.
  if (kind === 'endpoint' && !sources.includes('EDR (SentinelOne)')) {
    add('coverage', 'No EDR agent reporting', 26);
  }
  if (applicable.length > 1 && sources.length === 1) {
    add('coverage', `Single source only (${sources[0]})`, 12);
  }

  // ── Posture.
  const stale = staleDays(t, kind, id);
  if (stale > 21) add('posture', `Not observed for ${stale} days`, 14);

  if (kind === 'endpoint') {
    if (endpointOwner(t, id) === null) {
      add('posture', 'No resolvable owner', 10);
    }
    if (isServerEndpoint(t, id)) {
      add('classification', 'Server — shared blast radius', 8);
    }

    // ── Exposure.
    const vulns = vulnCount(t, id);
    if (vulns > 0) {
      // Square-root, not linear. Mean vuln count is ~12, so a linear term
      // saturated its own cap on nearly every machine and stopped discriminating
      // between them — it became a constant wearing a variable's clothes. Under
      // a root, 1 vuln scores 4 and 36 vulns scores 18: still monotonic, but the
      // marginal vulnerability stops mattering once a host is clearly neglected.
      add('exposure', `${vulns} open ${vulns === 1 ? 'vulnerability' : 'vulnerabilities'}`, Math.min(18, Math.round(Math.sqrt(vulns) * 3)));
    }

    // ── Tendency, inherited from the people on the machine. This is the hop
    // that makes an ordinary laptop rank above a bare server: the machine is
    // only as safe as whoever keeps clicking things on it.
    let inherited = 0;
    for (const link of usersOfEndpoint(t, id)) inherited += caseCount(t, link.userId);
    if (inherited > 0) {
      add('tendency', `${inherited} prior ${inherited === 1 ? 'case' : 'cases'} among its users`, Math.min(18, inherited * 6));
    }
  }

  if (kind === 'user') {
    const cases = caseCount(t, id);
    if (cases > 0) {
      add('tendency', `${cases} prior ${cases === 1 ? 'case' : 'cases'}`, Math.min(20, cases * 7));
    }
    if (advisoryCount(t, id) > 0) {
      add('exposure', 'Active travel advisory', 12);
    }
    if (isExternalUser(t, id)) {
      add('classification', 'External / guest account', 10);
    }

    /**
     * Exposure inherited from the machines this person uses.
     *
     * The missing half of the model. An endpoint already inherits tendency from
     * its users; without the reverse a person could never out-score their own
     * laptop, and the worst user in every tenant topped out at tier 2 — which
     * made the headline vertical the one place the threat lens had nothing
     * urgent to show.
     *
     * It is also just true: someone with three unmanaged, vulnerable machines is
     * a bigger problem than someone with one clean one, and no per-user field
     * anywhere records that.
     */
    const owned = endpointsOfUser(t, id);
    if (owned.length) {
      const unmonitored = owned.filter((e) => !seenBy(t, 'endpoint', e).includes('EDR (SentinelOne)')).length;
      if (unmonitored > 0) {
        add('coverage', `${unmonitored} of their ${owned.length} ${owned.length === 1 ? 'machine' : 'machines'} unmonitored`, Math.min(16, unmonitored * 9));
      }
      const ownedVulns = owned.reduce((sum, e) => sum + vulnCount(t, e), 0);
      if (ownedVulns > 0) {
        add('exposure', `${ownedVulns} open ${ownedVulns === 1 ? 'vulnerability' : 'vulnerabilities'} on their machines`, Math.min(16, Math.round(Math.sqrt(ownedVulns) * 2.5)));
      }
    }
  }

  // ── Customer classifications and tags. Business context beats CVSS: the same
  // finding on a PCI-scope host is not the same finding.
  const CLASS_WEIGHTS: Record<string, number> = {
    privileged_access: 18,
    pci_scope: 15,
    service_account: 11,
    shared_device: 8,
    byod: 8,
  };
  for (const c of entityClassifications(t, kind, id)) {
    const w = CLASS_WEIGHTS[c];
    // Through the same humaniser the filter panel uses, or one classification
    // reads two ways on one screen — "shared device" in a tier reason and
    // "Shared device" in the facet list, for the same stored key.
    if (w) add('classification', classificationLabel(c), w);
  }

  const TAG_WEIGHTS: Record<string, number> = {
    Executive: 14,
    Critical: 12,
    Production: 10,
    VIP: 10,
    Kiosk: 6,
    Contractor: 6,
  };
  for (const tag of entityTags(t, kind, id)) {
    const w = TAG_WEIGHTS[tag];
    if (w) add('classification', tag, w);
  }

  const score = Math.min(100, reasons.reduce((sum, r) => sum + r.weight, 0));
  /**
   * Thresholds calibrated against the real tenant shapes, not picked by feel.
   *
   * The measured target is roughly 3–5% tier 1 and 15–20% tier 2 on endpoints.
   * The first cut put 17–22% of ALL endpoints in tier 1, which is the failure
   * this whole design is supposed to avoid: a queue where a fifth of the estate
   * is "critical" is a queue nobody triages from, and it is exactly what makes
   * customers ignore vendor severity in the first place.
   *
   * 72 is deliberately out of reach for posture alone. An unmonitored host with
   * a dozen vulnerabilities scores ~44 and lands tier 2 — bad, worth attention,
   * not an emergency. Reaching tier 1 needs BUSINESS context on top: a
   * classification, an executive tag, a case history. That is the whole claim of
   * this tier — that context, not CVSS, is what makes something critical.
   */
  const tier: ThreatTier = score >= 72 ? 1 : score >= 48 ? 2 : score >= 26 ? 3 : 4;

  // Heaviest first — the reason list is read top-down and the top line is the
  // one that has to justify the tier on its own.
  reasons.sort((a, b) => b.weight - a.weight);
  return { tier, score, reasons };
}

/**
 * The worst entity of a kind, by threat score.
 *
 * Scans a BOUNDED window rather than the whole vertical: at the identity-heavy
 * profile that is 21,522 users, and a seed endpoint must not cost a full-tenant
 * scan. The real backend would answer this from an indexed score column; the
 * window keeps the prototype honest about that being a server-side ORDER BY, not
 * something the client sweeps.
 */
const THREAT_SCAN_WINDOW = 600;

export function pickWorstEntity(t: SyntheticTenant, kind: PivotNodeKind): number {
  const count = kind === 'user' ? t.userCount : kind === 'endpoint' ? t.endpointCount : 1;
  const limit = Math.min(count, THREAT_SCAN_WINDOW);
  let bestId = 1;
  let bestScore = -1;
  for (let id = 1; id <= limit; id++) {
    const s = threatOf(t, kind, id).score;
    if (s > bestScore) {
      bestScore = s;
      bestId = id;
    }
  }
  return bestId;
}

// ────────────────────────────────────────────────────────────────────────────
// Node construction
// ────────────────────────────────────────────────────────────────────────────

export function nodeId(kind: PivotNodeKind, id: number | string): string {
  return `${kind}:${id}`;
}

export function buildNode(
  t: SyntheticTenant,
  kind: PivotNodeKind,
  id: number,
  ring: PivotRing,
  confidence = 1,
): PivotNode {
  let label = '';
  let sublabel: string | null = null;
  let weight = 1;

  switch (kind) {
    case 'user':
      label = userName(t, id);
      sublabel = isExternalUser(t, id) ? 'Guest' : userDepartment(t, id);
      break;
    case 'endpoint':
      label = endpointName(t, id);
      sublabel = osName(t, endpointOs(t, id));
      break;
    case 'application':
      label = appName(t, id);
      weight = Math.round(appPopularity(id) * t.endpointCount);
      sublabel = `${weight.toLocaleString()} endpoints`;
      break;
    case 'operating_system':
      label = osName(t, id);
      weight = countEndpointsOnOs(t, id);
      sublabel = `${weight.toLocaleString()} devices`;
      break;
    case 'vulnerability': {
      // Read from the shared catalogue rather than re-deriving from the id, so a
      // CVE reads identically wherever it appears — on an endpoint's ring, as a
      // focus, or in the grid. Two different labels for one CVE is the fastest
      // way to make the whole board look invented.
      const cve = cveCatalog(t)[id];
      label = cve ? cve.cve : VULN_TITLES[id % VULN_TITLES.length];
      weight = cve ? countEndpointsOfCve(t, id) : 1;
      sublabel = cve
        ? `${SEVERITY_LABELS[cve.severity]} · CVSS ${cve.cvss} · ${weight.toLocaleString()} affected`
        : null;
      break;
    }
    case 'case':
      label = CASE_TITLES[id % CASE_TITLES.length];
      sublabel = `CASE-${10_000 + id}`;
      break;
    case 'advisory':
      label = `Travel advisory — ${ADVISORY_CITIES[id % ADVISORY_CITIES.length]}`;
      sublabel = 'Elevated risk';
      break;
    default:
      label = String(id);
  }

  const cve = kind === 'vulnerability' ? cveCatalog(t)[id] : undefined;

  return {
    node_id: nodeId(kind, id),
    kind,
    label,
    sublabel,
    ring,
    weight,
    confidence,
    is_aggregate: false,
    represents: null,
    // A CVE now has real members — every endpoint carrying the vulnerable
    // component — so it gets a drill query like any other hub.
    members_query: cve ? `kind=endpoint&cve=${id}` : null,
    pivotable: true,
    severity: cve?.severity ?? null,
    cvss: cve?.cvss ?? null,
    cve: cve?.cve ?? null,
    // Only on kinds that can carry them, and left UNDEFINED elsewhere rather
    // than empty — "this entity has no tags" and "this kind has no tags" are
    // different facts and the filter logic depends on telling them apart.
    ...(isFilterableKind(kind)
      ? { tags: entityTags(t, kind, id), classifications: entityClassifications(t, kind, id) }
      : {}),
  };
}

/**
 * Whether one entity passes the active filters.
 *
 * Unfilterable kinds pass unconditionally. That is the rule that keeps a filter
 * honest: narrowing endpoints to `pci_scope` must not delete the CVE ring on the
 * grounds that CVEs have no classifications. See `FILTERABLE_KINDS`.
 *
 * OR within a facet, AND across facets — an empty facet is no constraint.
 */
export function matchesFilters(
  t: SyntheticTenant,
  kind: PivotNodeKind,
  id: number,
  filters: PivotFilters | null,
): boolean {
  if (!filters || !hasActiveFilters(filters)) return true;
  if (!isFilterableKind(kind)) return true;

  // Tags carry no applicability declaration — they are free text and any
  // canonical entity may hold any of them — so every selected tag constrains
  // every filterable kind.
  if (filters.tags.length) {
    const tags = entityTags(t, kind, id);
    if (!filters.tags.some((want) => tags.includes(want))) return false;
  }

  // Classifications DO declare applicability, so the constraint is narrowed to
  // the selected values that can apply to this kind. If none can, this facet
  // says nothing about this kind and must not exclude it.
  if (filters.classifications.length) {
    const applicable = filters.classifications.filter((c) => classificationApplies(c, kind));
    if (applicable.length) {
      const cls = entityClassifications(t, kind, id);
      if (!applicable.some((want) => cls.includes(want))) return false;
    }
  }
  return true;
}

/**
 * Whether the active filters actually constrain this kind.
 *
 * The difference between "nothing here matched" and "this filter has nothing to
 * say about this kind". Drives the relation index's `constrained` flag, and
 * through it the empty state — a filter that constrains nothing on this entity
 * has not emptied the graph, it has simply not applied.
 */
export function filtersConstrain(kind: PivotNodeKind, filters: PivotFilters | null): boolean {
  if (!filters || !hasActiveFilters(filters)) return false;
  if (!isFilterableKind(kind)) return false;
  if (filters.tags.length) return true;
  return filters.classifications.some((c) => classificationApplies(c, kind));
}

function sourceNode(source: string, ownerKey: string, ring: PivotRing): PivotNode {
  return {
    node_id: nodeId('source_identity', `${ownerKey}|${source}`),
    kind: 'source_identity',
    label: source,
    sublabel: 'Source identity',
    ring,
    weight: 1,
    confidence: 1,
    is_aggregate: false,
    represents: null,
    members_query: null,
    pivotable: false,
  };
}

function aggregateNode(
  kind: PivotNodeKind,
  ring: PivotRing,
  total: number,
  membersQuery: string,
  labelNoun: string,
): PivotNode {
  return {
    node_id: nodeId('aggregate', `${ring}|${kind}`),
    kind: 'aggregate',
    label: `${total.toLocaleString()} ${labelNoun}`,
    sublabel: 'Open in list',
    ring,
    weight: total,
    confidence: 1,
    is_aggregate: true,
    represents: kind,
    members_query: membersQuery,
    pivotable: false,
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Relation lookups (lazy, symmetric)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Reverse index user → endpoints, built once per tenant.
 *
 * `endpointOwner` is cheap in the forward direction but the inverse needs a scan
 * of every endpoint. Doing that scan per user turns the entity picker into
 * O(users × endpoints) — 18 million operations at the large profile, which
 * locked the tab when the picker first rendered. One O(endpoints) pass, cached,
 * removes it.
 *
 * A real backend gets this for free: the edge table is indexed both ways.
 */
export interface UserEndpointLink {
  endpointId: number;
  role: 'primary' | 'observed';
  confidence: number;
}

const ownerIndexCache = new Map<ScaleProfileKey, Map<number, UserEndpointLink[]>>();

/**
 * Inverse of `usersOfEndpoint`, built in one pass over the endpoints and cached.
 *
 * Built from the same relation rather than generated independently, so the two
 * directions can never disagree: if a server lists you as an admin, that server
 * appears on your orbit, and pivoting back and forth is consistent.
 */
function ownerIndex(t: SyntheticTenant): Map<number, UserEndpointLink[]> {
  const cached = ownerIndexCache.get(t.key);
  if (cached) return cached;
  const index = new Map<number, UserEndpointLink[]>();
  for (let e = 1; e <= t.endpointCount; e++) {
    for (const link of usersOfEndpoint(t, e)) {
      const entry: UserEndpointLink = { endpointId: e, role: link.role, confidence: link.confidence };
      const list = index.get(link.userId);
      if (list) list.push(entry);
      else index.set(link.userId, [entry]);
    }
  }
  ownerIndexCache.set(t.key, index);
  return index;
}

/** Machines this user is associated with, primary links first. */
export function endpointLinksOfUser(t: SyntheticTenant, userId: number): UserEndpointLink[] {
  const all = ownerIndex(t).get(userId) ?? [];
  return [...all].sort((a, b) => (a.role === b.role ? 0 : a.role === 'primary' ? -1 : 1));
}

export function endpointsOfUser(t: SyntheticTenant, userId: number, limit = Infinity): number[] {
  const all = endpointLinksOfUser(t, userId).map((l) => l.endpointId);
  return limit === Infinity ? all : all.slice(0, limit);
}

export function appsOfEndpoint(t: SyntheticTenant, endpointId: number, limit = Infinity): number[] {
  const out: number[] = [];
  for (let a = 0; a < t.appCount; a++) {
    if (hasApp(t, endpointId, a)) {
      out.push(a);
      if (out.length >= limit) break;
    }
  }
  return out;
}

export function endpointsOfApp(t: SyntheticTenant, appRank: number, limit = Infinity): number[] {
  const out: number[] = [];
  for (let e = 1; e <= t.endpointCount; e++) {
    if (hasApp(t, e, appRank)) {
      out.push(e);
      if (out.length >= limit) break;
    }
  }
  return out;
}

export function endpointsOnOs(t: SyntheticTenant, osRank: number, limit = Infinity): number[] {
  const out: number[] = [];
  for (let e = 1; e <= t.endpointCount; e++) {
    if (endpointOs(t, e) === osRank) {
      out.push(e);
      if (out.length >= limit) break;
    }
  }
  return out;
}

function countEndpointsOnOs(t: SyntheticTenant, osRank: number): number {
  let n = 0;
  for (let e = 1; e <= t.endpointCount; e++) if (endpointOs(t, e) === osRank) n++;
  return n;
}

function countEndpointsOfApp(t: SyntheticTenant, appRank: number): number {
  let n = 0;
  for (let e = 1; e <= t.endpointCount; e++) if (hasApp(t, e, appRank)) n++;
  return n;
}

// ────────────────────────────────────────────────────────────────────────────
// Endpoint 1 — neighborhood
// ────────────────────────────────────────────────────────────────────────────

/**
 * Assemble one focus node's neighbourhood, applying the hub-collapse rule.
 *
 * Any ring wider than MAX_RING_MEMBERS becomes a single aggregate puck plus a
 * `collapsed` notice, so the canvas node count stays bounded no matter how large
 * the tenant or how popular the focus.
 *
 * `group` narrows the response to ONE relation group (ONE-3505). The other
 * groups are still counted and still appear in `relations` — only the drawing
 * narrows — and the focused group is drawn against the larger
 * ring's own legible capacity, because it is no longer sharing that circle.
 */
export function buildNeighborhood(
  t: SyntheticTenant,
  kind: PivotNodeKind,
  id: number,
  group: PivotNodeKind | null = null,
  filters: PivotFilters | null = null,
): PivotNeighborhood {
  const focus = buildNode(t, kind, id, 'identity');
  const nodes: PivotNode[] = [];
  const edges: PivotEdge[] = [];
  const collapsed: PivotCollapseNotice[] = [];
  const relations: PivotRelationSummary[] = [];
  let totalDegree = 0;

  const push = (n: PivotNode, provenance: PivotEdge['provenance'], confidence: number, label: string | null = null, from = focus.node_id) => {
    nodes.push(n);
    edges.push({ source: from, target: n.node_id, provenance, confidence, label });
  };

  /**
   * Whether this kind gets DRAWN.
   *
   * Degree is still counted for the kinds that do not — the relation index and
   * `total_degree` describe the entity, so they must not change shape depending
   * on which group happens to be in focus. Only the picture narrows.
   */
  const draws = (memberKind: PivotNodeKind): boolean => !group || group === memberKind;

  const filtering = hasActiveFilters(filters);

  /**
   * Record one relation group in the index, drawn or not.
   *
   * `matched` defaults to `total` so a kind that cannot carry the filtered
   * attribute reports itself as fully matching rather than as zero — the
   * difference between "this filter does not apply here" and "nothing here
   * passed", which are opposite findings.
   */
  const index = (
    memberKind: PivotNodeKind,
    ring: PivotRing,
    total: number,
    membersQuery: string | null,
    matched = total,
  ) => {
    if (total > 0) {
      relations.push({
        kind: memberKind,
        ring,
        total,
        matched,
        members_query: membersQuery,
        filterable: isFilterableKind(memberKind),
        constrained: filtersConstrain(memberKind, filters),
      });
    }
  };

  /** Members of one group that pass the active filters. */
  const passing = (memberKind: PivotNodeKind, ids: number[]): number[] =>
    filtersConstrain(memberKind, filters)
      ? ids.filter((m) => matchesFilters(t, memberKind, m, filters))
      : ids;

  /** Add a relation group, collapsing it if it is too wide to draw. */
  const addGroup = (
    members: number[],
    total: number,
    memberKind: PivotNodeKind,
    ring: PivotRing,
    provenance: PivotEdge['provenance'],
    confidenceOf: (memberId: number) => number,
    membersQuery: string,
    labelNoun: string,
    /**
     * Full member list for an exact filtered count, when the caller has one
     * cheaply. Without it the count falls back to the drawn sample, which would
     * under-report any group wide enough to have been sliced.
     */
    allMembers?: number[],
  ) => {
    totalDegree += total;

    const kept = passing(memberKind, members);
    // Count over the whole group where the caller could supply it; otherwise the
    // sample is all there is and the ratio is honest about being a sample.
    const matched = !filtersConstrain(memberKind, filters)
      ? total
      : allMembers
        ? passing(memberKind, allMembers).length
        : Math.round((kept.length / Math.max(1, members.length)) * total);

    index(memberKind, ring, total, membersQuery, matched);
    if (total === 0 || !draws(memberKind)) return;

    // Nothing survived the filter. Drawn as absence rather than as an empty
    // ring — the group still EXISTS, it just has no members you asked to see,
    // and that is a different statement from having none.
    if (filtersConstrain(memberKind, filters) && matched === 0) return;

    // A focused group has the ring to itself, so it earns whatever that ring
    // can legibly hold. Collapse is judged on what will be DRAWN, so a filter
    // that cuts a hub below the threshold gets to open it up — which is most of
    // the point of filtering a hairball.
    const drawTotal = filtersConstrain(memberKind, filters) ? matched : total;
    if (drawTotal > collapseThreshold(memberKind, group === memberKind ? ring : undefined)) {
      collapsed.push({ ring, kind: memberKind, shown: 0, total: drawTotal });
      push(aggregateNode(memberKind, ring, drawTotal, membersQuery, labelNoun), provenance, 1);
      return;
    }
    for (const m of kept) {
      push(buildNode(t, memberKind, m, ring, confidenceOf(m)), provenance, confidenceOf(m));
    }
  };

  /**
   * How many members to fetch for a kind on a given ring.
   *
   * Takes the ring because the focused budget is the ring's own capacity — see
   * `groupBudget`. Fetching more than this would only be thrown away by the
   * collapse rule below it.
   */
  const budget = (memberKind: PivotNodeKind, ring: PivotRing): number =>
    group === memberKind ? groupBudget(ring) : MAX_RING_MEMBERS;

  // Ring 1 — identity: which sources observed this entity.
  const sources = seenBy(t, kind, id);
  totalDegree += sources.length;
  index('source_identity', 'identity', sources.length, null);
  if (draws('source_identity')) {
    for (const s of sources) {
      push(sourceNode(s, `${kind}:${id}`, 'identity'), 'deterministic', 1, null);
    }
  }

  if (kind === 'user') {
    // Ring 2 — the keystone edge. Probabilistic, and drawn as such. A user can
    // be primary on their laptop and one of a dozen observed admins on a server.
    const links = endpointLinksOfUser(t, id);
    const confByEndpoint = new Map(links.map((l) => [l.endpointId, l.confidence]));
    addGroup(
      links.slice(0, budget('endpoint', 'related')).map((l) => l.endpointId), links.length,
      'endpoint', 'related', 'probabilistic',
      (e) => confByEndpoint.get(e) ?? 0.5,
      `kind=endpoint&owner=${id}`, 'endpoints',
      links.map((l) => l.endpointId),
    );

    // Ring 3 — exposure. Cases are parsed out of ticket descriptions, so they
    // are `derived` and render dashed.
    const cases = caseCount(t, id);
    addGroup(
      Array.from({ length: cases }, (_, i) => id * 10 + i), cases, 'case', 'exposure', 'derived',
      () => 0.45 + rand(t.key, 'caseconf', id) * 0.35,
      `kind=case&user=${id}`, 'cases',
    );

    const advisories = advisoryCount(t, id);
    addGroup(
      Array.from({ length: advisories }, (_, i) => id + i), advisories, 'advisory', 'exposure', 'deterministic',
      () => 1,
      `kind=advisory&user=${id}`, 'advisories',
    );
  }

  if (kind === 'endpoint') {
    // Every associated user, not just one. This is what makes
    // user → server → another user on that server navigable: a domain
    // controller shows its whole admin population, and any of them is one click
    // from becoming the new centre.
    const userLinks = usersOfEndpoint(t, id);
    totalDegree += userLinks.length;
    const keptLinks = filtersConstrain('user', filters)
      ? userLinks.filter((l) => matchesFilters(t, 'user', l.userId, filters))
      : userLinks;
    index('user', 'related', userLinks.length, `kind=user&endpoint=${id}`, keptLinks.length);
    if (draws('user') && keptLinks.length) {
      if (keptLinks.length > collapseThreshold('user', group === 'user' ? 'related' : undefined)) {
        collapsed.push({ ring: 'related', kind: 'user', shown: 0, total: keptLinks.length });
        push(
          aggregateNode('user', 'related', keptLinks.length, `kind=user&endpoint=${id}`, 'users'),
          'probabilistic', 1,
        );
      } else {
        for (const link of keptLinks) {
          push(
            buildNode(t, 'user', link.userId, 'related', link.confidence),
            'probabilistic',
            link.confidence,
            link.role === 'primary' ? 'primary user' : 'observed logon',
          );
        }
      }
    }

    // -1 means the tenant has no OS vertical at all (Defender-only). Skip the
    // relation entirely — it surfaces as a gap chip instead, which says "we
    // cannot see this" rather than silently implying there is nothing here.
    const osRank = endpointOs(t, id);
    if (osRank >= 0) {
      totalDegree += 1;
      const osKept = matchesFilters(t, 'operating_system', osRank, filters);
      index('operating_system', 'related', 1, `kind=operating_system&endpoint=${id}`, osKept ? 1 : 0);
      if (draws('operating_system') && osKept) {
        push(buildNode(t, 'operating_system', osRank, 'related', 1), 'deterministic', 1);
      }
    }

    // The wide one: 40–200 installed apps on a real endpoint. Always collapses
    // on the shared ring; drawn out properly once its group is focused.
    const appProbe = budget('application', 'related') + 1;
    const apps = appsOfEndpoint(t, id, appProbe);
    const appTotal = apps.length >= appProbe ? appsOfEndpoint(t, id).length : apps.length;
    addGroup(
      apps.slice(0, budget('application', 'related')), appTotal, 'application', 'related', 'deterministic',
      () => 1,
      `kind=application&endpoint=${id}`, 'applications',
      // Exact count needs the whole install list, not the drawn slice.
      filtering ? appsOfEndpoint(t, id) : undefined,
    );

    /**
     * Vulnerabilities as SEVERITY PUCKS, one per non-empty band.
     *
     * This is the whole point of the per-kind collapse policy. 39 uniform red
     * dots said only "there are 39" and ate the widest arc on the board saying
     * it. Three or four pucks — sized by count, coloured by band, captioned with
     * their CVSS range — say the same thing louder AND answer the question the
     * dots could not: how bad, and how many of each.
     *
     * Each puck is a doorway into the paged grid filtered to its band, which is
     * where CVE id, score, KEV and age actually belong.
     *
     * Focusing this group spreads the bands across the whole circle rather than
     * exploding them into dots — `collapseThreshold('vulnerability')` is 0 in
     * both modes on purpose, because the reason a CVE is unreadable as a dot has
     * nothing to do with how crowded the ring is.
     */
    const buckets = vulnsBySeverity(t, id);
    let vulnTotal = 0;
    for (const members of buckets.values()) vulnTotal += members.length;
    totalDegree += vulnTotal;
    index('vulnerability', 'exposure', vulnTotal, `kind=vulnerability&endpoint=${id}`);
    if (draws('vulnerability')) {
      for (const sev of SEVERITY_ORDER) {
        const members = buckets.get(sev);
        if (!members?.length) continue;
        const [lo, hi] = SEVERITY_CVSS_RANGE[sev];
        const kev = members.filter((m) => m.kev).length;
        push(
          {
            ...aggregateNode(
              'vulnerability',
              'exposure',
              members.length,
              `kind=vulnerability&endpoint=${id}&severity=${sev}`,
              'vulnerabilities',
            ),
            node_id: `vulnbucket:${id}:${sev}`,
            label: `${members.length} ${SEVERITY_LABELS[sev]}`,
            // KEV earns the caption when present — "actively exploited" outranks a
            // score range for deciding what to do first.
            sublabel: kev > 0 ? `${kev} actively exploited · CVSS ${lo}–${hi}` : `CVSS ${lo}–${hi}`,
            severity: sev,
          },
          'deterministic',
          1,
        );
      }
    }
  }

  if (kind === 'vulnerability') {
    // A CVE is a shared entity, so it has real blast radius: every endpoint
    // carrying the vulnerable component, and the component itself.
    const cve = cveCatalog(t)[id];
    if (cve) {
      const component = cve.target.kind;
      if (component === 'application' || component === 'operating_system') {
        totalDegree += 1;
        const kept = matchesFilters(t, component, cve.target.rank, filters);
        index(component, 'related', 1, null, kept ? 1 : 0);
        if (draws(component) && kept) {
          push(buildNode(t, component, cve.target.rank, 'related', 1), 'deterministic', 1, 'vulnerable component');
        }
      }
      const total = countEndpointsOfCve(t, id);
      const affectedAll = endpointsOfCve(t, id);
      const sample = endpointsOfCve(t, id, budget('endpoint', 'related'));
      addGroup(sample, total, 'endpoint', 'related', 'deterministic', () => 1, `kind=endpoint&cve=${id}`, 'endpoints', filtering ? affectedAll : undefined);

      /**
       * The people behind the machines.
       *
       * This file's own model of a CVE is `CVE → component → endpoints → users`
       * (see `SyntheticCve`), and the chain stopped one hop short: a CVE's
       * neighbourhood showed what it runs on and never who it reaches. "17
       * hosts" is a patching queue; "17 hosts, 9 people, two of them
       * executives" is a blast radius, and the second is the question this
       * surface exists to answer.
       *
       * DERIVED, and drawn dashed. Two hops of inference stacked — the CVE
       * reaches the endpoint by component, and the endpoint reaches the user by
       * XDR principal-name matching, which is the weakest join in the model. The
       * edge carries the WEAKER of the two confidences rather than an average:
       * averaging would let a certain first hop launder an uncertain second one,
       * and the whole point of carrying confidence is that it degrades along a
       * chain instead of being washed out by it.
       */
      const userConfidence = new Map<number, number>();
      for (const endpointId of affectedAll) {
        for (const link of usersOfEndpoint(t, endpointId)) {
          // Best evidence wins per user: someone who is primary on one affected
          // machine is not made less certain by also being an observed logon on
          // another.
          const prior = userConfidence.get(link.userId) ?? 0;
          if (link.confidence > prior) userConfidence.set(link.userId, link.confidence);
        }
      }
      const userIds = [...userConfidence.keys()];
      addGroup(
        userIds.slice(0, budget('user', 'related')), userIds.length,
        'user', 'related', 'derived',
        (u) => userConfidence.get(u) ?? 0.4,
        `kind=user&cve=${id}`, 'users',
        userIds,
      );
    }
  }

  if (kind === 'application') {
    // The hub case. At `large` this is thousands of endpoints for rank 0.
    const total = countEndpointsOfApp(t, id);
    const sample = endpointsOfApp(t, id, budget('endpoint', 'related'));
    addGroup(sample, total, 'endpoint', 'related', 'deterministic', () => 1, `kind=endpoint&app=${id}`, 'endpoints',
      filtering ? endpointsOfApp(t, id) : undefined);
  }

  if (kind === 'operating_system') {
    const total = countEndpointsOnOs(t, id);
    const sample = endpointsOnOs(t, id, budget('endpoint', 'related'));
    addGroup(sample, total, 'endpoint', 'related', 'deterministic', () => 1, `kind=endpoint&os=${id}`, 'endpoints',
      filtering ? endpointsOnOs(t, id) : undefined);
  }

  /**
   * The focus entity's threat tier — always, and only the focus.
   *
   * This used to be gated behind a `threat` lens that also RESEEDED you onto the
   * worst entity in the tenant. That made sense when the Visualizer was a
   * browse-from-nowhere surface; once entry became "open this entity" (ONE-3504)
   * and "open this case" (ONE-3507), a control that silently moved you somewhere
   * else and reset the trail was working against the product. The lens is gone.
   *
   * Tier is now what it always should have been: a property of the entity you
   * are looking at, shown in its detail panel alongside its other attributes.
   *
   * Only the FOCUS. The old code decorated every ring member too, at a posture
   * and history lookup each, to feed a fill colour that no longer exists —
   * nothing renders a tier for a node that is not selected, and selecting a node
   * makes it the focus, which computes its tier through this same line.
   * Aggregates never get one: a tier for "2,847 endpoints" would be an average,
   * and an averaged severity is the one number guaranteed to be wrong about
   * every member.
   */
  focus.threat = threatOf(t, focus.kind, id);

  /**
   * A group this entity has no members of falls through to the full
   * neighbourhood.
   *
   * It has to be a REBUILD, not just a null `focus_group`: the filter has
   * already run, so the response at this point is an entity with no
   * relationships at all. That is a meaningful state in this product — it is
   * what a genuinely unlinked entity looks like — so returning it here would
   * not read as an error, it would read as a finding, and it would be false.
   *
   * Reached only on a request that was already wrong (a stale link, a group
   * that emptied out since the page loaded), so the second pass costs nothing
   * on the path anyone actually takes. A real backend gets this for free: it
   * resolves the group against the degree index before selecting any members,
   * and simply does not apply one that is not there.
   */
  if (group && !relations.some((r) => r.kind === group)) {
    return buildNeighborhood(t, kind, id, null, filters);
  }

  return {
    focus,
    nodes,
    edges,
    relations: relations.sort((a, b) => b.total - a.total),
    /** Echo what was applied, never what was asked for. */
    focus_group: group,
    filters_applied: filters ?? EMPTY_FILTERS,
    collapsed,
    total_degree: totalDegree,
    gaps: gapsForKind(t, kind),
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Endpoint 5 — GET /v1/inventory/pivot/facets/   (ONE-3506)
// ────────────────────────────────────────────────────────────────────────────

/**
 * The tags and classifications present on ONE entity's relations, with counts.
 *
 * Scoped to the neighbourhood, not the tenant, and that is the whole design.
 * A tenant-wide facet list offers values that match nothing here — the user
 * picks one, the graph empties, and they learn the filter is untrustworthy. The
 * counts here are what will actually happen.
 *
 * Built by walking the SAME relation lists `buildNeighborhood` walks, so a facet
 * saying "Production (12)" and a filtered graph drawing 12 endpoints cannot
 * disagree. Deriving it from the drawn nodes instead would count only what
 * survived collapse — the facets for a hub would be computed from one puck.
 *
 * A real backend serves this as one GROUP BY over the edge table joined to the
 * tag/classification tables; it is the same traversal as the neighbourhood, with
 * a different projection.
 */
export function buildFacets(
  t: SyntheticTenant,
  kind: PivotNodeKind,
  id: number,
): PivotFacets {
  const tagCounts = new Map<string, number>();
  const clsCounts = new Map<string, number>();

  const tally = (memberKind: PivotNodeKind, ids: number[]) => {
    if (!isFilterableKind(memberKind)) return;
    for (const m of ids) {
      for (const tag of entityTags(t, memberKind, m)) {
        tagCounts.set(tag, (tagCounts.get(tag) ?? 0) + 1);
      }
      for (const cls of entityClassifications(t, memberKind, m)) {
        clsCounts.set(cls, (clsCounts.get(cls) ?? 0) + 1);
      }
    }
  };

  if (kind === 'user') {
    tally('endpoint', endpointsOfUser(t, id));
  } else if (kind === 'endpoint') {
    tally('user', usersOfEndpoint(t, id).map((l) => l.userId));
    tally('application', appsOfEndpoint(t, id));
    const os = endpointOs(t, id);
    if (os >= 0) tally('operating_system', [os]);
  } else if (kind === 'application') {
    tally('endpoint', endpointsOfApp(t, id));
  } else if (kind === 'operating_system') {
    tally('endpoint', endpointsOnOs(t, id));
  } else if (kind === 'vulnerability') {
    const affected = endpointsOfCve(t, id);
    tally('endpoint', affected);
    const users = new Set<number>();
    for (const e of affected) for (const l of usersOfEndpoint(t, e)) users.add(l.userId);
    tally('user', [...users]);
    const cve = cveCatalog(t)[id];
    if (cve && (cve.target.kind === 'application' || cve.target.kind === 'operating_system')) {
      tally(cve.target.kind, [cve.target.rank]);
    }
  }

  // Sorted by count so the values worth picking are the ones nearest the top.
  // Alphabetical would put "BYOD" above a tag matching ten times as much.
  const toValues = (
    counts: Map<string, number>,
    label: (v: string) => string,
    appliesTo?: (v: string) => PivotNodeKind[],
  ): PivotFacetValue[] =>
    [...counts.entries()]
      .map(([value, count]) => ({
        value,
        label: label(value),
        count,
        ...(appliesTo ? { applies_to: appliesTo(value) } : {}),
      }))
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  return {
    // Tags declare no applicability — free text, any canonical entity.
    tags: toValues(tagCounts, (v) => v),
    classifications: toValues(clsCounts, classificationLabel, classificationEntityTypes),
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Endpoint 2 — aggregate (cube face)
// ────────────────────────────────────────────────────────────────────────────

const LENS_COLUMNS: Record<PivotLens, PivotAxis> = {
  coverage: {
    dimension: 'lens', label: 'Coverage',
    values: [
      { key: 'single_source', label: 'Single source' },
      { key: 'multi_source', label: 'Multi-source' },
      { key: 'no_edr', label: 'No EDR' },
      { key: 'stale', label: 'Stale > 30d' },
    ],
  },
  risk: {
    dimension: 'lens', label: 'Risk',
    values: [
      { key: 'critical', label: 'Critical' },
      { key: 'high', label: 'High' },
      { key: 'medium', label: 'Medium' },
      { key: 'low', label: 'Low' },
      { key: 'none', label: 'None' },
    ],
  },
  cases: {
    dimension: 'lens', label: 'Cases',
    values: [
      { key: 'open', label: 'Open' },
      { key: 'closed_30d', label: 'Closed 30d' },
      { key: 'none', label: 'No cases' },
    ],
  },
  advisory: {
    dimension: 'lens', label: 'Advisory',
    values: [
      { key: 'active', label: 'Active advisory' },
      { key: 'recent', label: 'Recent travel' },
      { key: 'none', label: 'None' },
    ],
  },
  threat: {
    dimension: 'lens', label: 'Threat tier',
    values: [
      { key: 'tier_1', label: 'Tier 1 · Critical' },
      { key: 'tier_2', label: 'Tier 2 · High' },
      { key: 'tier_3', label: 'Tier 3 · Moderate' },
      { key: 'tier_4', label: 'Tier 4 · Low' },
    ],
  },
};

const ENTITY_ROWS: PivotAxis = {
  dimension: 'entity_type', label: 'Entity',
  values: [
    { key: 'user', label: 'Users' },
    { key: 'endpoint', label: 'Endpoints' },
    { key: 'application', label: 'Applications' },
    { key: 'operating_system', label: 'Operating systems' },
  ],
};

function sliceAxis(t: SyntheticTenant, slice: PivotSlice): PivotAxis {
  switch (slice) {
    case 'source': {
      const all = Array.from(new Set(Object.values(SOURCES).flat()));
      return { dimension: 'source', label: 'Source', values: all.map((s) => ({ key: s, label: s })) };
    }
    case 'tag':
      return { dimension: 'tag', label: 'Tag', values: TAGS.map((s) => ({ key: s, label: s })) };
    case 'classification':
      return {
        dimension: 'classification', label: 'Classification',
        values: CLASSIFICATIONS.map((s) => ({ key: s, label: s.replace(/_/g, ' ') })),
      };
    case 'os':
      return {
        dimension: 'os', label: 'Operating system',
        values: Array.from({ length: t.osCount }, (_, i) => ({ key: String(i), label: osName(t, i) })),
      };
  }
}

/** Population size for one entity vertical. */
function populationOf(t: SyntheticTenant, entityKey: string): number {
  switch (entityKey) {
    case 'user': return t.userCount;
    case 'endpoint': return t.endpointCount;
    case 'application': return t.appCount;
    case 'operating_system': return t.osCount;
    default: return 0;
  }
}

/**
 * Cohort count for one cell. Deterministic given the axes, and derived from
 * cohort proportions rather than by walking entities — which is precisely why
 * the cube is scale-invariant, and how the real GROUP BY will behave.
 */
function cellValue(t: SyntheticTenant, rowKey: string, colKey: string, population: number): number {
  const share = 0.04 + rand(t.key, 'cell', rowKey, colKey) * 0.42;
  return Math.round(population * share);
}

const MAX_AXIS_VALUES = 12;

/** Top-N + "Other" for a high-cardinality axis. Never silent — see caller. */
function truncateAxis(axis: PivotAxis): { axis: PivotAxis; truncated: boolean } {
  if (axis.values.length <= MAX_AXIS_VALUES) return { axis, truncated: false };
  return {
    axis: {
      ...axis,
      values: [
        ...axis.values.slice(0, MAX_AXIS_VALUES - 1),
        { key: '__other__', label: `Other (${axis.values.length - MAX_AXIS_VALUES + 1})` },
      ],
    },
    truncated: true,
  };
}

/** Population for an axis value, whichever dimension it belongs to. */
function axisPopulation(t: SyntheticTenant, axis: PivotAxis, key: string): number {
  if (axis.dimension === 'entity_type') return populationOf(t, key);
  return Math.round(t.userCount * (0.1 + rand(t.key, 'slicepop', key) * 0.5));
}

/**
 * Cohort count for one cell 30 days ago. Drifts from the current value by up to
 * ±35% so the delta faces carry a realistic mix of growth, decay and noise
 * rather than a uniform trend.
 */
function priorCellValue(t: SyntheticTenant, rowKey: string, colKey: string, current: number): number {
  const drift = (rand(t.key, 'prior', rowKey, colKey) - 0.5) * 0.7;
  return Math.max(0, Math.round(current * (1 - drift)));
}

/** Generic face builder: any row axis crossed with any column axis. */
function buildFace(
  t: SyntheticTenant,
  rowsIn: PivotAxis,
  colsIn: PivotAxis,
  tag: string,
  measure: CubeMeasure = 'current',
): PivotAggregate {
  const r = truncateAxis(rowsIn);
  const c = truncateAxis(colsIn);

  let maxValue = 0;
  const cells: PivotCell[][] = r.axis.values.map((row) => {
    const population = axisPopulation(t, r.axis, row.key);
    return c.axis.values.map((col) => {
      const current = cellValue(t, row.key, col.key, population);
      const value =
        measure === 'current' ? current : current - priorCellValue(t, row.key, col.key, current);
      // Delta faces scale on absolute change so both arms of the diverging ramp
      // share one scale — a −40 has to read as strongly as a +40.
      maxValue = Math.max(maxValue, Math.abs(value));
      return {
        value,
        total: population,
        members_query: `face=${tag}&row=${encodeURIComponent(row.key)}&col=${encodeURIComponent(col.key)}`,
      };
    });
  });

  return {
    rows: r.axis,
    columns: c.axis,
    cells,
    measure,
    max_value: maxValue,
    truncated_axis: r.truncated ? 'rows' : c.truncated ? 'columns' : null,
  };
}

export function buildAggregate(
  t: SyntheticTenant,
  lens: PivotLens,
  slice: PivotSlice,
  rowsAre: 'entity_type' | 'slice',
): PivotAggregate {
  const rows = rowsAre === 'entity_type' ? ENTITY_ROWS : sliceAxis(t, slice);
  return buildFace(t, rows, LENS_COLUMNS[lens], `${rowsAre}_lens`);
}

/**
 * All six faces in one pass.
 *
 * Near side (front / right / top): the three orthogonal projections as counts.
 * Far side (back / left / bottom): the same three as 30-day change.
 */
export function buildCube(t: SyntheticTenant, lens: PivotLens, slice: PivotSlice): PivotCube {
  const entities = ENTITY_ROWS;
  const lensAxis = LENS_COLUMNS[lens];
  const slices = sliceAxis(t, slice);
  return {
    faces: {
      entity_lens_current: buildFace(t, entities, lensAxis, 'entity_lens', 'current'),
      slice_lens_current: buildFace(t, slices, lensAxis, 'slice_lens', 'current'),
      entity_slice_current: buildFace(t, entities, slices, 'entity_slice', 'current'),
      entity_lens_delta: buildFace(t, entities, lensAxis, 'entity_lens', 'delta'),
      slice_lens_delta: buildFace(t, slices, lensAxis, 'slice_lens', 'delta'),
      entity_slice_delta: buildFace(t, entities, slices, 'entity_slice', 'delta'),
    },
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Entity browse / search — choosing a pivot focus
// ────────────────────────────────────────────────────────────────────────────

export function buildDegree(t: SyntheticTenant, kind: PivotNodeKind, id: number): PivotDegree {
  const n = buildNeighborhood(t, kind, id);
  const byRing: Record<PivotRing, number> = { identity: 0, related: 0, exposure: 0 };
  for (const c of n.collapsed) byRing[c.ring] += c.total;
  for (const node of n.nodes) if (!node.is_aggregate) byRing[node.ring] += 1;
  return { ref: { kind, id }, by_ring: byRing, total: n.total_degree };
}

// ────────────────────────────────────────────────────────────────────────────
// Endpoint 4 — members (OData paged)
// ────────────────────────────────────────────────────────────────────────────

/**
 * Page the real rows behind a collapsed hub or a drilled cube cell.
 *
 * Note this returns a PAGE, never the whole cohort — the aggregate puck in the
 * orbit is a doorway into the existing paged grid, not a promise to draw 5,000
 * nodes.
 */
export function buildMembers(
  t: SyntheticTenant,
  params: URLSearchParams,
  skip: number,
  top: number,
  search = '',
  filters: PivotFilters | null = null,
): { count: number; rows: PivotMemberRow[]; searchCapped: boolean } {
  // Cube-cell drills carry `face`/`row`/`col` rather than `kind`. When the row
  // axis is the entity vertical, the row key IS the kind — without this a
  // "Users × Single source" cell listed endpoints, because `kind` fell through
  // to its default.
  const rowKey = params.get('row') ?? '';
  const rowIsEntityKind = ['user', 'endpoint', 'application', 'operating_system'].includes(rowKey);
  const kind = (params.get('kind') ?? (rowIsEntityKind ? rowKey : 'endpoint')) as PivotNodeKind;
  let ids: number[] = [];

  const owner = params.get('owner');
  const app = params.get('app');
  const os = params.get('os');
  const endpoint = params.get('endpoint');
  const user = params.get('user');

  const cve = params.get('cve');

  if (kind === 'endpoint' && owner) ids = endpointsOfUser(t, Number(owner));
  else if (kind === 'user' && cve) {
    // Users reachable through the endpoints one CVE affects. Same derivation as
    // the neighbourhood's user ring, so the puck and the list cannot disagree.
    const seen = new Set<number>();
    for (const endpointId of endpointsOfCve(t, Number(cve))) {
      for (const link of usersOfEndpoint(t, endpointId)) seen.add(link.userId);
    }
    ids = [...seen];
  }
  else if (kind === 'user' && endpoint) ids = usersOfEndpoint(t, Number(endpoint)).map((l) => l.userId);
  else if (kind === 'endpoint' && app) ids = endpointsOfApp(t, Number(app));
  else if (kind === 'endpoint' && os) ids = endpointsOnOs(t, Number(os));
  else if (kind === 'application' && endpoint) ids = appsOfEndpoint(t, Number(endpoint));
  else if (kind === 'operating_system' && endpoint) {
    // An endpoint runs exactly one OS. Missing until now: the relation index
    // hands out `kind=operating_system&endpoint=` (ONE-3505) and nothing here
    // claimed it, so the query fell through to the cube-cell branch below and
    // came back with an invented slice of the OS population — a list of
    // operating systems this endpoint does not run.
    const rank = endpointOs(t, Number(endpoint));
    ids = rank >= 0 ? [rank] : [];
  }
  else if (kind === 'endpoint' && cve) {
    // Blast radius of one CVE — the reverse direction, now that a CVE is shared.
    ids = endpointsOfCve(t, Number(cve));
  } else if (kind === 'vulnerability' && endpoint) {
    // Catalogue ids, optionally narrowed to the severity puck that was clicked.
    const severity = params.get('severity') as VulnSeverity | null;
    const list = vulnsOfEndpoint(t, Number(endpoint));
    ids = (severity ? list.filter((c) => c.severity === severity) : list).map((c) => c.id);
  } else if (kind === 'case' && user) {
    ids = Array.from({ length: caseCount(t, Number(user)) }, (_, i) => Number(user) * 10 + i);
  } else if (kind === 'advisory' && user) {
    ids = Array.from({ length: advisoryCount(t, Number(user)) }, (_, i) => Number(user) + i);
  } else if (params.get('face') || rowKey) {
    // Cube-cell drill: a deterministic slice of the vertical's population,
    // sized to the cell so the rail's row count matches the number on the face.
    //
    // Gated on the cube's own parameters rather than left as a bare `else`. As
    // a catch-all it answered ANY unrecognised cohort with an invented
    // population slice, so a relation whose query nothing handled did not fail —
    // it returned plausible, wrong rows. That is the worst way for a prototype
    // to break, and it hid exactly that bug for `kind=operating_system&endpoint=`.
    const population = populationOf(t, kind === 'aggregate' ? 'user' : kind);
    const colKey = params.get('col') ?? '';
    const share = rowKey && colKey ? 0.04 + rand(t.key, 'cell', rowKey, colKey) * 0.42 : 0.2;
    const n = Math.max(1, Math.round(population * share));
    const firstId = kind === 'application' || kind === 'operating_system' ? 0 : 1;
    ids = Array.from({ length: n }, (_, i) => i + firstId);
  } else {
    // An unhandled cohort returns nothing rather than something plausible.
    console.warn('[pivot] no member resolver for cohort:', params.toString());
    ids = [];
  }

  const rowKind: PivotNodeKind = kind === 'aggregate' ? 'user' : kind;

  // The list is the other half of the graph, so it narrows by the same rule.
  // A puck reading "12 endpoints" that opens a list of 47 would make the filter
  // look broken in the one place the user went to check it.
  if (filtersConstrain(rowKind, filters)) {
    ids = ids.filter((m) => matchesFilters(t, rowKind, m, filters));
  }
  const toRow = (id: number): PivotMemberRow => {
    const node = buildNode(t, rowKind, id, 'related');
    const conf = rowKind === 'endpoint' && owner ? ownershipConfidence(t, id) : 1;
    const cve = rowKind === 'vulnerability' ? cveCatalog(t)[id] : undefined;
    return {
      id,
      kind: rowKind,
      name: node.label,
      // On a CVE row the title is the useful detail; severity and score get their
      // own sortable columns rather than being crushed into a string.
      detail: cve ? cve.title : (node.sublabel ?? ''),
      severity: cve?.severity ?? null,
      cvss: cve?.cvss ?? null,
      affected: cve ? countEndpointsOfCve(t, id) : null,
      kev: cve?.kev ?? false,
      confidence: Number(conf.toFixed(2)),
      source_count: seenBy(t, rowKind, id).length,
      last_seen: lastSeen(t, rowKind, id),
    };
  };

  // No search: page the id list and only materialise the visible rows. This is
  // the path that matters — a 5,700-endpoint hub costs 25 row builds, not 5,700.
  if (!search) {
    return { count: ids.length, rows: ids.slice(skip, skip + top).map(toRow), searchCapped: false };
  }

  // Search has to look at labels, so rows must be materialised to filter. Capped
  // because the mock does client-side what the real backend does in SQL; the cap
  // is surfaced rather than silently truncating.
  const SEARCH_MATERIALISE_CAP = 2_000;
  const searchCapped = ids.length > SEARCH_MATERIALISE_CAP;
  const needle = search.toLowerCase();
  const matched = ids
    .slice(0, SEARCH_MATERIALISE_CAP)
    .map(toRow)
    .filter((r) => r.name.toLowerCase().includes(needle) || r.detail.toLowerCase().includes(needle));

  return { count: matched.length, rows: matched.slice(skip, skip + top), searchCapped };
}

/** Entry points for the "pivot to a random starting entity" affordance. */
export function pickSeedUser(t: SyntheticTenant): number {
  for (let u = 1; u <= t.userCount; u++) {
    if (!isExternalUser(t, u) && endpointsOfUser(t, u, 1).length > 0) return u;
  }
  return 1;
}

/**
 * A representative entity of a given kind to land on.
 *
 * Landing on id 1 unconditionally is wrong: the first user in a tenant often
 * owns no endpoints, so the very first thing anyone saw was an orbit with three
 * source nodes and nothing else — the sparsest possible view of the feature's
 * headline use case. This picks an entity that actually has relationships.
 *
 * The real backend wants this endpoint too: "give me somewhere sensible to
 * start" is a genuine product need, not a prototype crutch.
 */
export function pickSeedEntity(t: SyntheticTenant, kind: PivotNodeKind): number {
  switch (kind) {
    case 'user':
      return pickSeedUser(t);
    case 'endpoint': {
      // Prefer an endpoint with a resolvable owner, so the keystone edge is
      // visible on the first screen.
      for (let e = 1; e <= t.endpointCount; e++) if (endpointOwner(t, e) !== null) return e;
      return 1;
    }
    case 'application':
      // Rank 0 is the hub — the case the collapse rule exists for.
      return 0;
    case 'operating_system':
      return 0;
    default:
      return 1;
  }
}
